/**
 * Core type definitions for EdgeDocs Client-First Architecture
 * 
 * These types define the fundamental data structures for local document management,
 * ensuring type safety and clear contracts between components.
 */

// ============================================================================
// CORE DOCUMENT STATE
// ============================================================================

export interface LocalDocumentState {
  // Document identification
  documentId?: string;             // Document ID for server operations
  
  // Core document data
  text: string;                    // Single source of truth for document text
  caretPosition: number;           // Global caret position (0 to text.length)
  selection?: SelectionRange | null; // Text selection for copy/paste/format
  
  // Version tracking
  version: number;                 // Local version counter
  lastServerSync: number;          // Timestamp of last server synchronization
  serverVersion: number;           // Last known server version
  
  // Legal document metadata
  attorney: AttorneyInfo;          // Current editing attorney
  documentType: LegalDocumentType; // Contract, Brief, Motion, etc.
  jurisdiction: Jurisdiction;      // Federal, State, International
  
  // Performance optimization
  isDirty: boolean;               // Has unsaved changes
  dirtyRegions: Set<TextRange>;   // Changed text ranges for incremental rendering
  
  // Document metadata
  created: Date;
  modified: Date;
  title?: string;
  matterNumber?: string;
  clientName?: string;
}

export interface SelectionRange {
  start: number;
  end: number;
  direction: 'forward' | 'backward';
}

export interface TextRange {
  start: number;
  end: number;
}

// ============================================================================
// LEGAL DOCUMENT METADATA
// ============================================================================

export interface AttorneyInfo {
  id: string;
  name: string;
  email: string;
  barNumber: string;
  firmName: string;
  role: 'partner' | 'associate' | 'paralegal' | 'clerk';
  seniority: number; // Years of practice for conflict resolution
  
  // Additional properties needed by legal features
  title?: string;        // Attorney title/designation
  firm?: string;         // Firm name (alias for firmName)
  address?: string;      // Attorney address
  phone?: string;        // Attorney phone number
  jurisdiction?: string; // Attorney's primary jurisdiction
}

export type LegalDocumentType = 
  | 'contract'
  | 'brief'
  | 'motion' 
  | 'pleading'
  | 'discovery'
  | 'correspondence'
  | 'memo'
  | 'opinion'
  | 'filing'
  | 'document'     // Add generic document type
  | 'imported';    // Add imported document type

export type Jurisdiction = 'federal' | 'california' | 'new_york' | 'texas' | 'florida' | 'all';

export interface JurisdictionInfo {
  type: 'federal' | 'state' | 'international';
  name: string; // "Federal District Court" | "California" | "European Union"
  rules: JurisdictionRules;
}

export interface JurisdictionRules {
  margins: { top: number; right: number; bottom: number; left: number };
  fonts: string[]; // Allowed font families
  lineSpacing: number;
  pageNumberFormat: string;
  maxPageLength?: number;
  requiredHeaders?: string[];
  requiredFooters?: string[];
}

// ============================================================================
// OPERATIONS & CHANGES
// ============================================================================

export type Operation = InsertOperation | DeleteOperation | FormatOperation | InsertBulkOperation;

export interface BaseOperation {
  id: string;
  type: string;
  version: number;
  attorney: AttorneyInfo;
  timestamp: Date;
  position: number;
}

export interface InsertOperation extends BaseOperation {
  type: 'insert';
  content: string;
  formatting?: TextFormatting;
}

export interface InsertBulkOperation extends BaseOperation {
  type: 'insertBulk';
  content: string;
  formatting?: TextFormatting;
}

export interface DeleteOperation extends BaseOperation {
  type: 'delete';
  start: number;
  end: number;
  deletedContent: string;
}

export interface FormatOperation extends BaseOperation {
  type: 'format';
  range: TextRange;
  formatting: TextFormatting;
  previousFormatting?: TextFormatting;
}

export interface TextFormatting {
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  textDecoration?: 'none' | 'underline' | 'strikethrough';
  color?: string;
  backgroundColor?: string;
}

// ============================================================================
// OPERATION RESULTS
// ============================================================================

export interface OperationResult {
  success: boolean;
  operation?: Operation;
  operationTime?: number; // Milliseconds
  dirtyRegions?: Set<TextRange>;
  error?: string;
  reason?: string;
}

// ============================================================================
// LEGAL CHANGE TRACKING
// ============================================================================

export interface LegalChange {
  id: string;
  operation: Operation;
  attorney: AttorneyInfo;
  timestamp: Date;
  
  // Legal document analysis
  documentSection: DocumentSection;
  legalImpact: LegalImpact;
  requiresApproval: boolean;
  
  // Compliance metadata
  jurisdiction: Jurisdiction;
  practiceArea: string;
  clientMatter: string;
  
  // Approval status
  status: 'pending' | 'approved' | 'rejected' | 'under_review';
  approver?: AttorneyInfo;
  approvalDate?: Date;
  rejectionReason?: string;
}

export interface DocumentSection {
  type: 'clause' | 'citation' | 'signature' | 'header' | 'footer' | 'body';
  number?: string;
  title?: string;
  legalSignificance: 'low' | 'medium' | 'high' | 'critical';
}

export interface LegalImpact {
  level: 'formatting' | 'substantive' | 'structural' | 'critical';
  description: string;
  affectedClauses?: string[];
  citationChanges?: boolean;
  signatureBlockAffected?: boolean;
}

// ============================================================================
// PERFORMANCE & RENDERING
// ============================================================================

export interface RenderingContext {
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  devicePixelRatio: number;
  viewportWidth: number;
  viewportHeight: number;
}

export interface GlyphMetrics {
  character: string;
  width: number;
  height: number;
  ascent: number;
  descent: number;
  advance: number;
  fontFamily: string;
  fontSize: number;
}

export interface LayoutData {
  characterPositions: CharacterPosition[];
  lineBreaks: number[];
  pageBreaks: number[];
  totalWidth: number;
  totalHeight: number;
}

export interface CharacterPosition {
  index: number;        // Character index in document
  x: number;           // Pixel X coordinate
  y: number;           // Pixel Y coordinate
  width: number;       // Character width
  height: number;      // Character height
  baseline: number;    // Baseline Y coordinate
}

// ============================================================================
// COLLABORATION
// ============================================================================

export interface Collaborator {
  attorney: AttorneyInfo;
  caretPosition: number;
  selection?: SelectionRange;
  lastActivity: Date;
  isActive: boolean;
  color: string; // For cursor rendering
}

export interface RemoteOperation {
  // Base operation properties
  id: string;
  type: 'insert' | 'delete' | 'format';
  version: number;
  timestamp: Date;
  attorney: AttorneyInfo;
  
  // Remote-specific properties
  sourceAttorney: AttorneyInfo;
  remoteVersion: number;
  transformedAgainst: string[]; // Operation IDs this was transformed against
  
  // Operation-specific properties (union)
  position?: number;     // For insert operations
  content?: string;      // For insert operations
  start?: number;        // For delete operations
  end?: number;          // For delete operations
  deletedContent?: string; // For delete operations
  range?: TextRange;     // For format operations
  formatting?: any;      // For format operations
}

export interface ConflictResolution {
  conflictId: string;
  localOperation: Operation;
  remoteOperation: RemoteOperation;
  resolution: 'accept_local' | 'accept_remote' | 'merge' | 'manual_review';
  resolvedBy: AttorneyInfo;
  resolutionReason: string;
}

// ============================================================================
// IMPORT/EXPORT
// ============================================================================

export interface ImportResult {
  success: boolean;
  document: LocalDocumentState;
  originalFormat: 'docx' | 'pdf' | 'txt';
  layoutPreserved: boolean;
  importMetadata: ImportMetadata;
  warnings?: string[];
  errors?: string[];
}

export interface ImportMetadata {
  originalFileName: string;
  importDate: Date;
  originalFileSize: number;
  preservedElements: PreservedElements;
  ocrQuality?: number; // For PDF imports
  pageCount?: number;
  extractedText?: number; // Character count
}

export interface PreservedElements {
  fonts: number;
  paragraphs: number;
  headers: number;
  footers: number;
  tables: number;
  images: number;
  hyperlinks: number;
  bookmarks: number;
  footnotes: number;
}

export interface ExportOptions {
  format: 'docx' | 'pdf';
  preserveOriginalLayout: boolean;
  includeTrackChanges: boolean;
  includeComments: boolean;
  legalMetadata: boolean;
  courtCompliance?: Jurisdiction;
  confidentialityLevel?: 'public' | 'confidential' | 'attorney_client_privileged';
}

// ============================================================================
// ERROR HANDLING
// ============================================================================

export interface DocumentError {
  code: string;
  message: string;
  severity: 'warning' | 'error' | 'critical';
  operation?: Operation;
  recovery?: 'retry' | 'rollback' | 'manual_intervention';
  timestamp: Date;
}

export interface PerformanceMetrics {
  operationTime: number;
  renderTime: number;
  memoryUsage: number;
  syncLatency: number;
  frameRate: number;
}

// ============================================================================
// ENHANCED STATE MANAGEMENT TYPES (Phase 1)
// ============================================================================

/**
 * Enhanced document engine state with performance optimizations
 */
export interface EnhancedDocumentEngineState {
  // Core document state
  text: string;
  caretPosition: number;
  selection: SelectionRange | null;
  version: number;
  isDirty: boolean;
  
  // Performance metrics
  performanceMetrics: PerformanceMetrics;
  
  // Backend service status (enhanced)
  backendServices: {
    overall: ServiceStatus;
    cache: ServiceStatus;
    harfbuzz: ServiceStatus;
    fontManager: ServiceStatus;
    backendFont: ServiceStatus;
  };
  
  // HarfBuzz metrics (enhanced)
  harfbuzzMetrics: {
    totalRequests: number;
    successfulRequests: number;
    failedRequests: number;
    pendingRequests: number;
    optimalBatchSize: number;
    averageProcessingTime: number;
    cacheHitRate: number;
    lastBatchTime: number;
    fontSubstitutionCount: number;
    batchProcessingTime: number;
  };
  
  // Font substitutions
  fontSubstitutions: FontSubstitution[];
  
  // State management metadata
  stateMetadata: {
    lastUpdate: number;
    updateCount: number;
    errorCount: number;
    performanceScore: number;
  };
}

/**
 * Service status with detailed health information
 */
export interface ServiceStatus {
  ready: boolean;
  healthy: boolean;
  lastCheck: number;
  latency?: number;
  errorRate?: number;
  version?: string;
  details?: Record<string, any>;
}

/**
 * Font substitution information
 */
export interface FontSubstitution {
  requested: string;
  actual: string;
  reason: string;
  timestamp: Date;
  impact: 'low' | 'medium' | 'high';
}

/**
 * State update event types for the event system
 */
export type StateUpdateEvent = 
  | 'document-changed'
  | 'caret-moved'
  | 'selection-changed'
  | 'performance-updated'
  | 'service-status-changed'
  | 'harfbuzz-metrics-updated'
  | 'font-substitution-added'
  | 'error-occurred';

/**
 * State update payload for batched updates
 */
export interface StateUpdatePayload {
  type: StateUpdateEvent;
  timestamp: number;
  data: any;
  source: string;
}

/**
 * Performance thresholds for monitoring
 */
export interface PerformanceThresholds {
  maxStateUpdateTime: number;    // ms
  maxRenderTime: number;         // ms
  maxMemoryUsage: number;        // MB
  minCacheHitRate: number;       // percentage
  maxErrorRate: number;          // percentage
}

/**
 * State coordinator configuration
 */
export interface StateCoordinatorConfig {
  batchDelay: number;
  performanceThresholds: PerformanceThresholds;
  enableErrorBoundary: boolean;
  enablePerformanceMonitoring: boolean;
  maxEventHistory: number;
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

export type EventCallback<T = any> = (data: T) => void;

export interface EventEmitter {
  on<T>(event: string, callback: EventCallback<T>): void;
  off<T>(event: string, callback: EventCallback<T>): void;
  emit<T>(event: string, data: T): void;
}

export interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

// ============================================================================
// CONFIGURATION
// ============================================================================

export interface ClientFirstConfig {
  // Performance targets
  maxOperationTime: number;     // 5ms
  maxRenderTime: number;        // 16ms
  syncInterval: number;         // 200ms
  
  // Memory limits
  maxDocumentSize: number;      // 10MB
  maxUndoStackSize: number;     // 1000 operations
  maxGlyphCacheSize: number;    // 100MB
  
  // Legal compliance
  auditLogging: boolean;
  changeTracking: boolean;
  approvalWorkflow: boolean;
  
  // Collaboration
  maxCollaborators: number;     // 10
  presenceUpdateInterval: number; // 1000ms
  conflictResolutionTimeout: number; // 5000ms
}

export const DEFAULT_CONFIG: ClientFirstConfig = {
  maxOperationTime: 5,
  maxRenderTime: 16,
  syncInterval: 200,
  maxDocumentSize: 10 * 1024 * 1024, // 10MB
  maxUndoStackSize: 1000,
  maxGlyphCacheSize: 100 * 1024 * 1024, // 100MB
  auditLogging: true,
  changeTracking: true,
  approvalWorkflow: true,
  maxCollaborators: 10,
  presenceUpdateInterval: 1000,
  conflictResolutionTimeout: 5000
};
